import { CalendarDaysIcon } from 'lucide-react';
import { createElement } from 'react';

import { defineModule } from '@/shared/registry/types';

import { useCalendarCommands } from './commands';
import { EventSearchResult } from './components/extensions';
import { useCalendarNavCounter } from './hooks';
import { LazyEventPanel, LazyGroupCalendarTab, LazyProfileCalendarTab } from './lazy';
import { setupCalendar } from './realtime';

/**
 * `calendar` module (docs/PRODUCT.md "Calendar", docs/modules/calendar.md): month, week, day and agenda views under
 * `/calendar`, the invitations inbox `/calendar/invitations`, the `event:<id>` / `event:new` side panels, the group tab
 * and profile tab `calendar`, the sidebar counter of pending invitations, ⌘K actions and the search renderer of events.
 */
export const module = defineModule({
  id: 'calendar',
  routes: [
    {
      path: 'calendar/invitations',
      lazy: async () => ({ Component: (await import('./pages/InvitationsPage')).default }),
    },
    {
      path: 'calendar/*',
      handle: { layout: 'full' },
      lazy: async () => ({ Component: (await import('./pages/CalendarPage')).default }),
    },
  ],
  nav: [
    {
      id: 'calendar',
      labelKey: 'calendar:nav.title',
      icon: CalendarDaysIcon,
      to: '/calendar',
      order: 40,
      section: 'main',
      useCounter: useCalendarNavCounter,
    },
  ],
  sidePanels: [{ type: 'event', component: LazyEventPanel, size: 'lg', titleKey: 'calendar:panel.title' }],
  groupTabs: [
    {
      id: 'calendar',
      labelKey: 'calendar:tabs.group',
      order: 30,
      element: (groupId) => createElement(LazyGroupCalendarTab, { groupId }),
    },
  ],
  profileTabs: [
    {
      id: 'calendar',
      labelKey: 'calendar:tabs.profile',
      order: 50,
      element: (userId) => createElement(LazyProfileCalendarTab, { userId }),
    },
  ],
  searchRenderers: { event: EventSearchResult },
  commands: [
    {
      id: 'calendar-actions',
      headingKey: 'calendar:commands.heading',
      order: 40,
      useItems: useCalendarCommands,
    },
  ],
  setup: setupCalendar,
});
